import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { DragulaModule } from 'ng2-dragula/ng2-dragula';

import { InlineSpinnerLoaderComponent } from './components/inline-spinner-loader/inline-spinner-loader.component';
import { DateTimePickerComponent } from './components/date-time-picker/date-time-picker.component';
import { AutocompleteComponent } from './components/autocomplete/autocomplete.component';
import { DropdownComponent } from './components/dropdown/dropdown.component';
import { ModalComponent } from './components/modal/modal.component';
import { LoaderButton } from './components/loader-button/loader-button.component';
import { AppPreviewContainerComponent } from './components/app-preview-container/app-preview-container.component';
import { FakeUiLoaderComponent } from './components/fake-ui-loader/fake-ui-loader.component';
import { PodPreviewComponent } from './components/pod-preview/pod-preview.component';
import { TooltipButtonComponent } from './components/tooltip-button/tooltip-button.component';
import { TooltipButtonDropdownComponent } from './components/tooltip-button-dropdown/tooltip-button-dropdown.component';
import { RemainingTextIndicatorComponent } from './components/remaining-text-indicator/remaining-text-indicator.component';
import { ContentEditorComponent } from './components/content-editor/content-editor.component';
import { PagePreviewComponent } from './components/page-preview/page-preview.component';
import { QuestionPreviewComponent } from './components/page-preview/question-preview/question-preview.component';
import { RichTextEditorComponent } from './components/rich-text-editor/rich-text-editor.component';
import { FileUploadComponent } from './components/file-upload/file-upload.component';
import { ProgressBarComponent } from './components/progress-bar/progress-bar.component';
import { UploadProgressBarComponent } from './components/upload-progress-bar/upload-progress-bar.component';
import { TabBarComponent } from './components/tab-bar/tab-bar.component';
import { QuestionsEditorComponent } from './components/questions-editor/questions-editor.component';
import { QuestionBuilderComponent } from './components/question-builder/question-builder.component';
import { InnerLeftNavComponent } from './components/inner-left-nav/inner-left-nav.component';
import { FeatureBubbleComponent } from './components/feature-bubble/feature-bubble.component';
import { DashboardWhatsNew } from './components/dashboard-whats-new/dashboard-whats-new.component';
import { DashboardHelpTutorial } from './components/dashboard-help-tutorial/dashboard-help-tutorial.component';
import { StatusIndicatorComponent } from './components/status-indicator/status-indicator.component';
import { TableHeaderCellComponent } from './components/table-header/table-header-cell/table-header-cell.component';
import { AppUsersEditorComponent } from './components/app-users-editor/app-users-editor.component';
import { AppUsersBuilderComponent } from './components/app-users-builder/app-users-builder.component';
import { ContentItemComponent } from './components/content-editor/content-item/content-item.component';
import { SimpleScrollableTableComponent } from './components/simple-scrollable-table/simple-scrollable-table.component';

import { TrailingTextDirective } from './components/trailing-text/trailing-text.directive';
import { DelayedSearchDirective } from './components/delayed-search/delayed-search.directive';
import { AutosizeDirective } from './components/autosize/autosize.directive';

import { SecureUrlPipe } from './pipes/secure-url.pipe';
import { DatexPipe } from './pipes/date.pipe';
import { CapitalizePipe } from './pipes/capitalize.pipe';
import { Safe } from './pipes/safe-html.pipe';
import { HoverComponent } from './components/hover/hover.component';
import { ToastComponent } from './components/toast/toast.component';
import { LoaderComponent } from './components/loader/loader.component';
import { TopNavComponent } from './components/top-nav/top-nav.component';
import { UserAvatarComponent } from './components/user-avatar/user-avatar.component';
import { LeftNavComponent } from './components/left-nav/left-nav.component';
import { HelpAndSupportComponent } from './components/help-and-support/help-and-support.component';
import { ApplicationBubbleComponent } from './components/application-bubble/application-bubble.component';
import { LeftNavItemComponent } from './components/left-nav/left-nav-item.component';
import { FroalaEditorModule, FroalaViewModule } from 'angular-froala-wysiwyg';
import { ImagePreloadService } from 'app/shared/services/image-preload/image-preload.service';
import { TooltipComponent } from './components/tooltip/tooltip.component';
import { ContentImageCropperComponent } from './components/content-image-cropper/content-image-cropper.component';
import { DraggableImageAreaComponent } from './components/draggable-image-area/draggable-image-area.component';
import { ContentEditorImageModalComponent } from './components/content-editor/content-editor-image-modal/content-editor-image-modal.component';
import { ImageLibraryComponent } from './components/image-library/image-library.component';
import { DraggableImageWrapperComponent } from './components/draggable-image-wrapper/draggable-image-wrapper.component';
import { ImageLibraryCropperModalComponent } from './components/image-library-cropper-modal/image-library-cropper-modal.component';
import { PromoLibraryCropperModalComponent } from 'app/shared/components/image-library-cropper-modal/promo-image-library-cropper-modal/promo-library-cropper-modal.component';
import { ModalService } from 'app/shared/services/modal/modal.service';
import { AutosizeService } from './components/autosize/autosize.service';
import { SimpleDeleteListComponent } from './components/simple-delete-list/simple-delete-list.component';
import { DeleteModalComponent } from '../screens/togglit/application/features/delete-modal/delete-modal.component';
import { WalkthroughService } from 'app/shared/services/walkthrough/walkthrough.service';
import { ContentEditorDocumentModalComponent } from './components/content-editor/content-editor-document-modal/content-editor-document-modal.component';
import { TableComponent } from './components/table/table.component';
import { TableCellComponent } from './components/table/table-cell/table-cell.component';
import { TableRowComponent } from './components/table/table-row/table-row.component';
import { TableHeaderComponent } from 'app/shared/components/table-header/table-header.component';
import { TableBodyComponent } from './components/table/table-body/table-body.component';
import { TimePickerComponent } from 'app/shared/components/time-picker/time-picker.component';

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DragulaModule,
    FroalaEditorModule.forRoot(), FroalaViewModule.forRoot()
  ],
  declarations: [
    InlineSpinnerLoaderComponent,
    DateTimePickerComponent,
    AutocompleteComponent,
    DropdownComponent,
    ModalComponent,
    LoaderButton,
    AppPreviewContainerComponent,
    FakeUiLoaderComponent,
    PodPreviewComponent,
    TooltipButtonComponent,
    TooltipButtonDropdownComponent,
    RemainingTextIndicatorComponent,
    ContentEditorComponent,
    QuestionPreviewComponent,
    PagePreviewComponent,
    RichTextEditorComponent,
    FileUploadComponent,
    ProgressBarComponent,
    UploadProgressBarComponent,
    TabBarComponent,
    QuestionsEditorComponent,
    QuestionBuilderComponent,
    FeatureBubbleComponent,
    InnerLeftNavComponent,
    TableHeaderCellComponent,
    AppUsersEditorComponent,
    AppUsersBuilderComponent,
    DelayedSearchDirective,
    TrailingTextDirective,
    AutosizeDirective,
    SecureUrlPipe,
    DatexPipe,
    Safe,
    CapitalizePipe,
    DashboardWhatsNew,
    DashboardHelpTutorial,
    StatusIndicatorComponent,
    HoverComponent,
    ToastComponent,
    LoaderComponent,
    TopNavComponent,
    UserAvatarComponent,
    LeftNavComponent,
    LeftNavItemComponent,
    HelpAndSupportComponent,
    ApplicationBubbleComponent,
    ContentItemComponent,
    TooltipComponent,
    SimpleScrollableTableComponent,
    ContentImageCropperComponent,
    DraggableImageAreaComponent,
    ContentEditorImageModalComponent,
    ImageLibraryComponent,
    DraggableImageWrapperComponent,
    ImageLibraryCropperModalComponent,
    PromoLibraryCropperModalComponent,
    SimpleDeleteListComponent,
    ContentEditorDocumentModalComponent,
    TimePickerComponent,
    DeleteModalComponent,
    ContentEditorDocumentModalComponent,
    TableComponent,
    TableCellComponent,
    TableRowComponent,
    TableHeaderComponent,
    TableBodyComponent
  ],
  exports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DragulaModule,
    InlineSpinnerLoaderComponent,
    DateTimePickerComponent,
    AutocompleteComponent,
    DropdownComponent,
    ModalComponent,
    LoaderButton,
    AppPreviewContainerComponent,
    FakeUiLoaderComponent,
    PodPreviewComponent,
    TooltipButtonComponent,
    TooltipButtonDropdownComponent,
    RemainingTextIndicatorComponent,
    ContentEditorComponent,
    QuestionPreviewComponent,
    PagePreviewComponent,
    RichTextEditorComponent,
    FileUploadComponent,
    ProgressBarComponent,
    UploadProgressBarComponent,
    TabBarComponent,
    QuestionsEditorComponent,
    QuestionBuilderComponent,
    FeatureBubbleComponent,
    InnerLeftNavComponent,
    TableHeaderComponent,
    TableHeaderCellComponent,
    AppUsersEditorComponent,
    AppUsersBuilderComponent,
    DelayedSearchDirective,
    TrailingTextDirective,
    AutosizeDirective,
    SecureUrlPipe,
    DatexPipe,
    Safe,
    CapitalizePipe,
    DashboardWhatsNew,
    DashboardHelpTutorial,
    StatusIndicatorComponent,
    HoverComponent,
    ToastComponent,
    LoaderComponent,
    TopNavComponent,
    UserAvatarComponent,
    LeftNavComponent,
    LeftNavItemComponent,
    HelpAndSupportComponent,
    ApplicationBubbleComponent,
    ContentItemComponent,
    TooltipComponent,
    SimpleScrollableTableComponent,
    ContentImageCropperComponent,
    DraggableImageAreaComponent,
    DraggableImageWrapperComponent,
    ContentEditorImageModalComponent,
    ContentEditorDocumentModalComponent,
    ImageLibraryComponent,
    ImageLibraryCropperModalComponent,
    PromoLibraryCropperModalComponent,
    SimpleDeleteListComponent,
    SimpleScrollableTableComponent,
    DeleteModalComponent,
    TableComponent,
    TableCellComponent,
    TableRowComponent,
    TableBodyComponent,
    TimePickerComponent ],
  providers: [ ImagePreloadService,
               WalkthroughService ]
})
export class SharedModule { }



// WEBPACK FOOTER //
// /app/src/app/shared/shared.module.ts